import React, { useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { FileJson, Store, CheckCircle2, AlertCircle, Loader2, Database, Layers, UtensilsCrossed, Settings2, ChefHat, Link2, MessageCircle, ArrowRight, ArrowLeft, UserCircle, KeyRound, Mail, RefreshCw } from 'lucide-react';
// ─── Types ───────────────────────────────────────────────────
interface RestaurantPayload {
  name: string;
  slug: string;
  whatsapp_number?: string;
  [key: string]: any;
}
interface ParsedPayload {
  restaurant: RestaurantPayload;
  categories: any[];
  items: any[];
  options: any[];
  stations: any[];
}
interface CreateResult {
  success: boolean;
  result?: {
    success: boolean;
    restaurant_id: number;
    restaurant_slug: string;
    menu_import: {
      success: boolean;
      restaurant_id: number;
      categories_inserted: number;
      items_inserted: number;
      options_inserted: number;
    };
    stations_created: number;
    station_mappings_created: number;
  };
}
interface ValidationState {
  status: 'idle' | 'valid' | 'error';
  errors: string[];
  counts: {
    categories: number;
    items: number;
    options: number;
    stations: number;
  };
}
// ─── Summary Card ────────────────────────────────────────────
function SummaryMetric({
  icon: Icon,
  label,
  value
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
}) {
  return <div className="bg-[#0B0B0B] border border-white/5 rounded-xl p-4 text-center" data-id="element-3392">
      <Icon className="w-4 h-4 text-white/30 mx-auto mb-2" data-id="element-3393" />
      <div className="text-2xl font-bold text-white" data-id="element-3394">{value}</div>
      <div className="text-[10px] text-white/40 uppercase tracking-widest mt-1" data-id="element-3395">
        {label}
      </div>
    </div>;
}
// ─── Page Component ──────────────────────────────────────────
export function EnrollmentRestaurantPage() {
  const navigate = useNavigate();
  const [jsonInput, setJsonInput] = useState('');
  const [parsedData, setParsedData] = useState<ParsedPayload | null>(null);
  const [validation, setValidation] = useState<ValidationState>({
    status: 'idle',
    errors: [],
    counts: {
      categories: 0,
      items: 0,
      options: 0,
      stations: 0
    }
  });
  const [restaurantFields, setRestaurantFields] = useState({
    name: '',
    slug: '',
    whatsapp_number: ''
  });
  const [adminFields, setAdminFields] = useState({
    full_name: '',
    email: '',
    temporary_password: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createResult, setCreateResult] = useState<CreateResult | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    const specials = '!@#$%';
    let pwd = '';
    for (let i = 0; i < 10; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    pwd += specials.charAt(Math.floor(Math.random() * specials.length));
    setAdminFields(prev => ({
      ...prev,
      temporary_password: pwd
    }));
  };
  // ─── Validate ────────────────────────────────────────────
  const handleValidate = () => {
    setParsedData(null);
    setCreateResult(null);
    setCreateError(null);
    const errors: string[] = [];
    if (!jsonInput.trim()) {
      setValidation({
        status: 'error',
        errors: ['El JSON está vacío.'],
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    let data: any;
    try {
      data = JSON.parse(jsonInput);
    } catch {
      setValidation({
        status: 'error',
        errors: ['El JSON no es válido. Revisa la sintaxis.'],
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    // Structural validation
    if (!data.restaurant || typeof data.restaurant !== 'object' || Array.isArray(data.restaurant)) {
      errors.push('"restaurant" debe ser un objeto.');
    } else {
      if (!data.restaurant.name) errors.push('"restaurant.name" es obligatorio.');
      if (!data.restaurant.slug) errors.push('"restaurant.slug" es obligatorio.');
    }
    if (!Array.isArray(data.categories)) {
      errors.push('"categories" debe ser un array.');
    } else if (data.categories.length === 0) {
      errors.push('"categories" no puede estar vacío.');
    }
    if (!Array.isArray(data.items)) {
      errors.push('"items" debe ser un array.');
    } else if (data.items.length === 0) {
      errors.push('"items" no puede estar vacío.');
    }
    if (!Array.isArray(data.options)) {
      errors.push('"options" debe ser un array.');
    }
    if (!Array.isArray(data.stations)) {
      errors.push('"stations" debe ser un array.');
    } else if (data.stations.length === 0) {
      errors.push('"stations" no puede estar vacío.');
    }
    if (errors.length > 0) {
      setValidation({
        status: 'error',
        errors,
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    const counts = {
      categories: data.categories.length,
      items: data.items.length,
      options: data.options.length,
      stations: data.stations.length
    };
    setParsedData(data as ParsedPayload);
    setRestaurantFields({
      name: data.restaurant.name || '',
      slug: data.restaurant.slug || '',
      whatsapp_number: data.restaurant.whatsapp_number || ''
    });
    setValidation({
      status: 'valid',
      errors: [],
      counts
    });
  };
  // ─── Create ──────────────────────────────────────────────
  const handleCreateRestaurant = async (parsedJsonPayload: ParsedPayload) => {
    setIsCreating(true);
    setCreateError(null);
    setCreateResult(null);
    try {
      console.log('[EnrollmentRestaurant] Sending payload:', parsedJsonPayload);
      // Get user session for Authorization
      const {
        data: {
          session
        },
        error: sessionError
      } = await supabase.auth.getSession();
      if (sessionError) {
        setCreateError(`Error obteniendo sesión: ${sessionError.message}`);
        setIsCreating(false);
        return;
      }
      if (!session?.access_token) {
        setCreateError('Tu sesión expiró. Inicia sesión nuevamente en /admin/login');
        setIsCreating(false);
        return;
      }
      console.log('[EnrollmentRestaurant] Session:', {
        hasSession: true,
        accessToken: 'present',
        userId: session.user?.id ?? null
      });
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-restaurant-from-menu`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify(parsedJsonPayload)
      });
      const data = await response.json();
      console.log('[EnrollmentRestaurant] Response:', {
        status: response.status,
        data
      });
      if (!response.ok) {
        console.error('[EnrollmentRestaurant] Backend error:', data);
        const details = Array.isArray(data?.details) && data.details.length > 0 ? `\n\nDetalles:\n- ${data.details.join('\n- ')}` : '';
        setCreateError((data?.error || data?.message || `Error del servidor (${response.status})`) + details);
        return;
      }
      if (!data?.success) {
        setCreateError(data?.error || data?.details || 'Error al crear el restaurante.');
        return;
      }
      setCreateResult(data as CreateResult);
    } catch (error: any) {
      console.error('[EnrollmentRestaurant] Fetch error:', error);
      setCreateError(error instanceof Error ? error.message : 'Ocurrió un error inesperado al crear el restaurante.');
    } finally {
      setIsCreating(false);
    }
  };
  // ─── Render ──────────────────────────────────────────────
  if (createResult?.success && createResult.result) {
    const res = createResult.result;
    return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex flex-col items-center justify-center p-6" data-id="element-3396">
        <div className="max-w-2xl w-full space-y-8 text-center" data-id="element-3397">
          <div className="w-24 h-24 bg-[#006847]/20 rounded-full flex items-center justify-center mx-auto" data-id="element-3398">
            <CheckCircle2 className="w-12 h-12 text-[#00a870]" data-id="element-3399" />
          </div>
          <div data-id="element-3400">
            <h1 className="text-3xl font-black tracking-tight" data-id="element-3401">
              ¡Restaurante Creado!
            </h1>
            <p className="text-white/50 mt-2" data-id="element-3402">
              El restaurante y su menú han sido configurados exitosamente.
            </p>
          </div>

          <div className="bg-[#141414] border border-[#006847]/30 rounded-2xl p-6 text-left space-y-6" data-id="element-3403">
            <div className="flex items-center justify-between pb-4 border-b border-white/5" data-id="element-3404">
              <div data-id="element-3405">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-3406">
                  Restaurante
                </p>
                <p className="text-xl font-bold text-white mt-1" data-id="element-3407">
                  {restaurantFields.name}
                </p>
              </div>
              <div className="text-right" data-id="element-3408">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-3409">
                  Slug
                </p>
                <p className="text-lg font-mono text-[#00a870] mt-1" data-id="element-3410">
                  {res.restaurant_slug}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-3411">
              <SummaryMetric icon={Layers} label="Categorías" value={res.menu_import.categories_inserted} data-id="element-3412" />
              <SummaryMetric icon={UtensilsCrossed} label="Productos" value={res.menu_import.items_inserted} data-id="element-3413" />
              <SummaryMetric icon={Settings2} label="Opciones" value={res.menu_import.options_inserted} data-id="element-3414" />
              <SummaryMetric icon={ChefHat} label="Estaciones" value={res.stations_created} data-id="element-3415" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4" data-id="element-3416">
            <button onClick={() => {
            setCreateResult(null);
            setJsonInput('');
            setParsedData(null);
            setValidation({
              status: 'idle',
              errors: [],
              counts: {
                categories: 0,
                items: 0,
                options: 0,
                stations: 0
              }
            });
          }} className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl font-bold transition-colors" data-id="element-3417">
              Crear otro
            </button>
            <button onClick={() => navigate(`/admin/${res.restaurant_slug}`)} className="px-8 py-4 bg-[#006847] hover:bg-[#005a3d] text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2" data-id="element-3418">
              Ver restaurante <ArrowRight className="w-5 h-5" data-id="element-3419" />
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans selection:bg-[#006847] selection:text-white" data-id="element-3420">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 space-y-8" data-id="element-3421">
        {/* Header */}
        <header className="flex items-start gap-4" data-id="element-3422">
          <button onClick={() => navigate('/admin/enrollment')} className="w-10 h-10 bg-[#141414] border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors shrink-0 mt-2" data-id="element-3423">
            <ArrowLeft className="w-5 h-5" data-id="element-3424" />
          </button>
          <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center shrink-0" data-id="element-3425">
            <Store className="w-7 h-7 text-[#006847]" data-id="element-3426" />
          </div>
          <div data-id="element-3427">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-id="element-3428">
              Crear restaurante desde menú
            </h1>
            <p className="text-white/40 text-sm mt-1 max-w-xl" data-id="element-3429">
              Pega el JSON generado por el parser para crear restaurante, menú y
              KDS automáticamente a través de la Edge Function.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-3430">
          {/* ─── Left: JSON Input ─── */}
          <div className="lg:col-span-7 space-y-6" data-id="element-3431">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/20" data-id="element-3432">
              <div className="flex items-center justify-between mb-4" data-id="element-3433">
                <h2 className="text-base font-semibold flex items-center gap-2" data-id="element-3434">
                  <FileJson className="w-5 h-5 text-white/40" data-id="element-3435" />
                  Payload JSON
                </h2>
                <button onClick={handleValidate} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm font-medium transition-all active:scale-95" data-id="element-3436">
                  Validar JSON
                </button>
              </div>

              <textarea value={jsonInput} onChange={e => setJsonInput(e.target.value)} placeholder="Pega aquí el JSON generado por el sistema..." className="w-full h-[420px] bg-[#0B0B0B] border border-white/10 rounded-xl p-4 font-mono text-[13px] leading-relaxed text-white/80 placeholder:text-white/15 focus:outline-none focus:border-[#006847]/60 focus:ring-1 focus:ring-[#006847]/40 resize-y transition-colors" spellCheck={false} data-id="element-3437" />

              {/* Validation Error */}
              {validation.status === 'error' && <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-3438">
                  <div className="flex items-center gap-2 text-red-400 mb-2" data-id="element-3439">
                    <AlertCircle className="w-4 h-4" data-id="element-3440" />
                    <strong className="text-sm font-semibold" data-id="element-3441">
                      Errores de validación
                    </strong>
                  </div>
                  <ul className="list-disc list-inside text-sm text-red-300/80 space-y-1" data-id="element-3442">
                    {validation.errors.map((err, i) => <li key={i} data-id="element-3443">{err}</li>)}
                  </ul>
                </div>}
            </div>
          </div>

          {/* ─── Right: Preview & Actions ─── */}
          <div className="lg:col-span-5 space-y-6" data-id="element-3444">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 sticky top-8" data-id="element-3445">
              <h2 className="text-base font-semibold mb-6 flex items-center gap-2" data-id="element-3446">
                <Store className="w-5 h-5 text-white/40" data-id="element-3447" />
                Datos del Restaurante
              </h2>

              <div className="space-y-5" data-id="element-3448">
                {/* Editable Fields */}
                <div data-id="element-3449">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3450">
                    Nombre
                  </label>
                  <input type="text" value={restaurantFields.name} onChange={e => setRestaurantFields(prev => ({
                  ...prev,
                  name: e.target.value
                }))} disabled={validation.status !== 'valid'} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="Ej: Capricho de Canela" data-id="element-3451" />
                </div>

                <div data-id="element-3452">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3453">
                    Slug (URL)
                  </label>
                  <div className="flex relative" data-id="element-3454">
                    <span className="inline-flex items-center px-4 rounded-l-xl border border-r-0 border-white/10 bg-white/5 text-white/40 text-sm" data-id="element-3455">
                      noizacr.com/
                    </span>
                    <input type="text" value={restaurantFields.slug} onChange={e => setRestaurantFields(prev => ({
                    ...prev,
                    slug: e.target.value.toLowerCase().replace(/\s+/g, '-')
                  }))} disabled={validation.status !== 'valid'} className="flex-1 bg-[#0B0B0B] border border-white/10 rounded-r-xl px-4 py-3 text-sm text-white font-mono focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="capricho-de-canela" data-id="element-3456" />
                  </div>
                </div>

                <div data-id="element-3457">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3458">
                    WhatsApp (Opcional)
                  </label>
                  <div className="relative" data-id="element-3459">
                    <MessageCircle className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-3460" />
                    <input type="text" value={restaurantFields.whatsapp_number} onChange={e => setRestaurantFields(prev => ({
                    ...prev,
                    whatsapp_number: e.target.value
                  }))} disabled={validation.status !== 'valid'} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="50688888888" data-id="element-3461" />
                  </div>
                </div>

                {/* ─── Admin del Restaurante ─── */}
                <div className="pt-6 border-t border-white/10" data-id="element-3462">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-white/70" data-id="element-3463">
                    <UserCircle className="w-4 h-4 text-white/30" data-id="element-3464" />
                    Admin del restaurante
                  </h3>

                  <div className="space-y-4" data-id="element-3465">
                    <div data-id="element-3466">
                      <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3467">
                        Nombre completo
                      </label>
                      <input type="text" value={adminFields.full_name} onChange={e => setAdminFields(prev => ({
                      ...prev,
                      full_name: e.target.value
                    }))} disabled={validation.status !== 'valid'} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="Juan Pérez" data-id="element-3468" />
                    </div>

                    <div data-id="element-3469">
                      <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3470">
                        Correo electrónico
                      </label>
                      <div className="relative" data-id="element-3471">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-3472" />
                        <input type="email" value={adminFields.email} onChange={e => setAdminFields(prev => ({
                        ...prev,
                        email: e.target.value
                      }))} disabled={validation.status !== 'valid'} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="juan@email.com" data-id="element-3473" />
                      </div>
                    </div>

                    <div data-id="element-3474">
                      <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3475">
                        Contraseña temporal
                      </label>
                      <div className="flex gap-2" data-id="element-3476">
                        <div className="relative flex-1" data-id="element-3477">
                          <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-3478" />
                          <input type="text" value={adminFields.temporary_password} onChange={e => setAdminFields(prev => ({
                          ...prev,
                          temporary_password: e.target.value
                        }))} disabled={validation.status !== 'valid'} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white font-mono focus:outline-none focus:border-[#006847] disabled:opacity-50" placeholder="Noiza1234!" data-id="element-3479" />
                        </div>
                        <button type="button" onClick={generatePassword} disabled={validation.status !== 'valid'} className="px-3 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors disabled:opacity-50 shrink-0" title="Generar contraseña" data-id="element-3480">
                          <RefreshCw className="w-4 h-4 text-white/50" data-id="element-3481" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Counts Summary */}
                {validation.status === 'valid' && <div className="pt-6 border-t border-white/10" data-id="element-3482">
                    <div className="flex items-center gap-2 text-[#00a870] mb-4" data-id="element-3483">
                      <CheckCircle2 className="w-4 h-4" data-id="element-3484" />
                      <span className="text-sm font-bold" data-id="element-3485">JSON Válido</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-3486">
                      <SummaryMetric icon={Layers} label="Categorías" value={validation.counts.categories} data-id="element-3487" />
                      <SummaryMetric icon={UtensilsCrossed} label="Productos" value={validation.counts.items} data-id="element-3488" />
                      <SummaryMetric icon={Settings2} label="Opciones" value={validation.counts.options} data-id="element-3489" />
                      <SummaryMetric icon={ChefHat} label="Estaciones" value={validation.counts.stations} data-id="element-3490" />
                    </div>
                  </div>}

                {/* Create Error */}
                {createError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-3491">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-3492" />
                    <div className="text-sm whitespace-pre-line" data-id="element-3493">
                      {createError}
                    </div>
                  </div>}

                {/* Action Button */}
                <button onClick={() => {
                if (!parsedData) return;
                if (validation.status !== 'valid') {
                  handleValidate();
                  return;
                }
                const payloadToSend = {
                  ...parsedData,
                  restaurant: {
                    ...parsedData.restaurant,
                    name: restaurantFields.name,
                    slug: restaurantFields.slug,
                    whatsapp_number: restaurantFields.whatsapp_number
                  },
                  admin_user: adminFields.email ? {
                    full_name: adminFields.full_name,
                    email: adminFields.email,
                    temporary_password: adminFields.temporary_password
                  } : undefined
                };
                handleCreateRestaurant(payloadToSend as any);
              }} disabled={validation.status !== 'valid' || isCreating} className="w-full py-4 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-3494">
                  {isCreating ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-3495" />
                      Creando restaurante...
                    </> : <>
                      <Store className="w-5 h-5" data-id="element-3496" />
                      Crear restaurante
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}